// Stadium Trial — balance telemetry (fire-and-forget, OUT of the reward path).
//
// Writes ONE append-only row per fight (pve_fights) and one per boost draft
// (pve_boost_drafts) so the Metabase dashboard can measure win-rates, boost
// pick-rates and difficulty calibration per patch (config_version).
//
// Hard rules:
//   - NEVER awaited in the reward/progression path: a failed insert must never
//     block savePveStageWin / creditPveRewards nor be visible to the player.
//   - Rows are auto-descriptive: they carry the enemy tuning + boost effect
//     values that were LIVE at fight time, so patching content never breaks or
//     pollutes past data (segment by config_version instead).
//   - Everything player-strength related is stamped per row (level_gap,
//     power_ratio, relevance) so overleveled farmers (e.g. a Lv13 clearing the
//     Lv1-3 tier) can be excluded from balance reads with one filter.

import { sb } from "../../../lib/supabase";
import type { EquipmentStats } from "../../league-duel/engine/combat";
import type { CombatSequence } from "./pveEngine";
import {
  PVE_CONFIG_VERSION,
  estimateFullEquippedPower,
  getEncounter,
  getTier,
} from "../content/pveTiers";
import { getBoost } from "../content/pveBoosts";

// ── Data-relevance classification ─────────────────────────────────────────────
// "Is this fight useful for balancing?" A row is on_target only when the player
// roughly matches the tier's tuning target; otherwise the outcome says more
// about the player's over/under-gearing than about the content's difficulty.
// Raw level_gap & power_ratio are stored too, so these thresholds can be
// re-tuned in Metabase without re-logging anything.
export type PveFightRelevance = "on_target" | "overpowered" | "underpowered";

/** |level_gap| at/over this → out of the tuning target. */
export const RELEVANCE_LEVEL_GAP = 2;
/** player_power / full-epic ref at the tier max level, at/over this → overpowered. */
export const RELEVANCE_POWER_RATIO_HIGH = 1.25;
/** …at/under this → underpowered. */
export const RELEVANCE_POWER_RATIO_LOW = 0.75;

export interface PveFightRelevanceInfo {
  levelGap: number;     // 0 = within the tier's levelRange; ±N = outside by N levels
  powerRatio: number;   // playerPower / full-epic reference at the tier MAX level
  relevance: PveFightRelevance;
}

/** Pure — classify a player against a tier's tuning target ([min,max] levelRange). */
export function classifyFightRelevance(
  playerLevel: number,
  playerPower: number,
  tier: number
): PveFightRelevanceInfo {
  const range = getTier(tier)?.levelRange ?? [1, 100];
  const [lo, hi] = range;
  const levelGap = playerLevel < lo ? playerLevel - lo : playerLevel > hi ? playerLevel - hi : 0;
  // Boss tuning targets a full-EPIC player at the tier's max level (see pveTiers).
  const refPower = Math.max(1, estimateFullEquippedPower(hi, "epic"));
  const powerRatio = playerPower / refPower;

  let relevance: PveFightRelevance = "on_target";
  if (levelGap >= RELEVANCE_LEVEL_GAP || powerRatio >= RELEVANCE_POWER_RATIO_HIGH) {
    relevance = "overpowered";
  } else if (levelGap <= -RELEVANCE_LEVEL_GAP || powerRatio <= RELEVANCE_POWER_RATIO_LOW) {
    relevance = "underpowered";
  }
  return { levelGap, powerRatio: Math.round(powerRatio * 100) / 100, relevance };
}

// ── Fight log ─────────────────────────────────────────────────────────────────

export interface PveFightLogInput {
  profileId: string;
  runId: string | null;
  tier: number;
  stage: number;
  playerLevel: number;
  playerStats: EquipmentStats;   // the stats the fight was simulated with
  activeBoosts: string[];        // boost ids active during the fight ([] in variant B)
  sequence: CombatSequence;      // simulateCombat output OR finalizeInteractiveCombat output
  // A/B test — which engine resolved this fight, and its variant-specific
  // metrics (interactiveVariantStats for 'spells': energy, autos, casts, banking).
  variant?: string;              // 'control' | 'spells' (null on legacy rows)
  variantStats?: Record<string, unknown>;
}

/**
 * Log one fight, best-effort. Call AFTER the fight resolves, NEVER await it in
 * the reward path (`void logPveFight(...)`). Swallows every error by design —
 * telemetry must never affect gameplay.
 */
export function logPveFight(input: PveFightLogInput): void {
  try {
    const { sequence, playerStats } = input;
    const { frames, result } = sequence;
    const encounter = getEncounter(input.tier, input.stage);

    const playerPower = Math.round(
      playerStats.physique + playerStats.technique + playerStats.vitesse + playerStats.defense
    );
    const { levelGap, powerRatio, relevance } = classifyFightRelevance(
      input.playerLevel, playerPower, input.tier
    );

    // Derive fight detail from the frames (the simulation is the source of truth).
    let dmgDealt = 0, dmgTakenRaw = 0, playerCrits = 0, enemyCrits = 0;
    let healTotal = 0, riposteCount = 0, lastStandFired = false;
    for (const f of frames) {
      if (f.attacker === "player") {
        dmgDealt += f.damage;
        if (f.crit) playerCrits++;
        if (f.counter) riposteCount++;
        if (f.heal) healTotal += f.heal;
      } else {
        dmgTakenRaw += f.damage; // pre-shield damage (real HP lost = result.damageReceived)
        if (f.crit) enemyCrits++;
        if (f.lastStand) lastStandFired = true;
      }
    }

    // Live balance snapshot: what you would patch, as it was for THIS fight.
    const boostEffects: Record<string, unknown> = {};
    for (const id of input.activeBoosts) {
      const b = getBoost(id);
      if (b) boostEffects[id] = b.effects;
    }

    void sb.insertEvent("pve_fights", {
      profile_id: input.profileId,
      run_id: input.runId,
      config_version: PVE_CONFIG_VERSION,
      tier: input.tier,
      stage: input.stage,
      enemy_id: encounter?.enemyId ?? "unknown",
      kind: encounter?.kind ?? "mob",
      outcome: result.win ? "win" : "loss",
      win_chance_est: result.winChance,
      player_level: input.playerLevel,
      player_power: playerPower,
      level_gap: levelGap,
      power_ratio: powerRatio,
      relevance,
      active_boosts: input.activeBoosts,
      variant: input.variant ?? null,
      variant_stats: input.variantStats ?? null,
      context: {
        // Fight detail
        turns: frames.length ? frames[frames.length - 1].turn : 0,
        hp_start: frames[0]?.playerHp ?? null,
        hp_end: frames.length ? frames[frames.length - 1].playerHp : null,
        player_max_hp: sequence.playerMaxHp,
        boss_max_hp: sequence.bossMaxHp,
        dmg_dealt: dmgDealt,
        dmg_taken_raw: dmgTakenRaw,
        hp_lost: result.damageReceived,
        player_crits: playerCrits,
        enemy_crits: enemyCrits,
        heal_total: healTotal,
        riposte_count: riposteCount,
        last_stand_fired: lastStandFired,
        // Balance snapshot (as tuned at fight time)
        player_stats: playerStats,
        enemy_stat_block: encounter?.statBlock ?? null,
        enemy_mob_budget: encounter?.mobBudget ?? null,
        enemy_damage_multiplier: encounter?.damageMultiplier ?? 1,
        enemy_effects: encounter?.effects ?? [],
        boost_effects: boostEffects,
        xp_earned: result.xpEarned,
        gold_earned: result.goldEarned,
      },
    });
  } catch (e) {
    console.warn("[PveTelemetry] logPveFight skipped:", e);
  }
}

// ── Boost draft log ───────────────────────────────────────────────────────────
// Pick-rate = chosen / offered. Impossible to reconstruct without logging the
// offer itself, so log the draft the moment the player validates (or skips) it.

export interface PveBoostDraftLogInput {
  profileId: string;
  runId: string | null;
  tier: number;
  stage: number;
  offered: string[];             // the boost ids proposed by the draft
  chosen: string | null;         // null = draft skipped/dismissed
  replaced?: string | null;      // boost dropped when swapping at max capacity
  activeBefore: string[];        // boosts already held before this draft
}

/** Log one boost draft, best-effort. Same rules as logPveFight. */
export function logPveBoostDraft(input: PveBoostDraftLogInput): void {
  try {
    void sb.insertEvent("pve_boost_drafts", {
      profile_id: input.profileId,
      run_id: input.runId,
      config_version: PVE_CONFIG_VERSION,
      tier: input.tier,
      stage: input.stage,
      offered: input.offered,
      chosen: input.chosen,
      replaced: input.replaced ?? null,
      active_before: input.activeBefore,
    });
  } catch (e) {
    console.warn("[PveTelemetry] logPveBoostDraft skipped:", e);
  }
}
